import {
    Alert,
    Box,
    Button, Checkbox,
    Dialog,
    DialogContent, FormControlLabel, Stack,
    Typography
} from "@mui/material";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    removeItemsFromSelection,
    selectAgeRange, selectConstructs, selectInstruments, setFieldType,
} from "../../dataWranglerSlice";
import { useWizardQuery } from "../../../../api/dataWranglerApiSlice";
import { CancelOutlined } from "@mui/icons-material";
import { colours } from "../../../../StyleConstants";
import { selectSelectedDatabase } from '../../../common/databaseSlice';

type Props = {
    open: boolean;
    onClose: (b: boolean) => void;
}

function RunQueryModal(props: Props) {
    const { open, onClose } = props;
    const dispatch = useDispatch();

    const [shadowMatrixIncluded, setShadowMatrixIncluded] = useState(false);

    const selectedAgeRanges = useSelector(selectAgeRange);
    const selectedConstructs = useSelector(selectConstructs);
    const selectedInstruments = useSelector(selectInstruments);

    const selectedDatabase = useSelector(selectSelectedDatabase);
    const { data: wizardData } = useWizardQuery({ database: selectedDatabase });
    const withShadowData = Object.keys(wizardData?.allInstruments || []).some((instrument: any) => wizardData?.allInstruments[instrument].shadowMatrix);

    const selectedInstrumentsSet = new Set(selectedInstruments);
    const selectedVisitsSet = new Set(selectedAgeRanges);
    const constructsWithNoSelectedInstrument = selectedConstructs.filter((selectedConstructKey: any) => {
        const constructObject = wizardData?.constructs[selectedConstructKey];
        if (!constructObject) return true;
        const constructInstruments = Object.keys(constructObject || {});
        return !constructInstruments.some(constructInstrument => selectedInstrumentsSet.has(constructInstrument));
    });

    const unnecessaryVisits = selectedAgeRanges.filter((visit: string) => {
        const isVisitIncludedOnInstrumentVisits = selectedInstruments.some((instr: string) => {
            const instrumentObject = wizardData.allInstruments[instr];
            if (instrumentObject.staticInstrument) return false;
            const instrumentVisits = instrumentObject.visitLabels;
            return instrumentVisits?.includes(visit) || false;
        });
        return !isVisitIncludedOnInstrumentVisits;
    });

    const instrumentsWithNoSelectedVisits = selectedInstruments.filter((instrument: string) => {
        const instrumentData = wizardData.allInstruments[instrument];
        return !instrumentData || instrumentData?.visitLabels?.filter((visit: string) => selectedVisitsSet.has(visit)).length === 0;
    });

    const warnings = [
        {
            condition: unnecessaryVisits.length > 0,
            title: "Warning: Selected Sessions Not Associated with Instruments",
            text: "Some of the selected sessions are not linked to any instruments. This may result in an incomplete data output. Please adjust your selection if necessary."
        },
        {
            condition: instrumentsWithNoSelectedVisits.length > 0,
            title: "Warning: Selected Instruments with No Associated Sessions",
            text: "Some of the selected instruments are not associated with any sessions. This may result in an incomplete data output. Please adjust your selection if necessary."
        },
        {
            condition: constructsWithNoSelectedInstrument.length > 0,
            title: "Warning: Selected Research Domains with No Instruments",
            text: "You have selected research domains that are not linked to any instruments. This may result in an incomplete data output. Please review your selection if necessary."
        }
    ]

    const runQuery = () => {
        dispatch(removeItemsFromSelection({
            visits: unnecessaryVisits,
            instruments: instrumentsWithNoSelectedVisits,
            constructs: constructsWithNoSelectedInstrument
        }));
        dispatch(setFieldType(shadowMatrixIncluded ? 'shadow' : 'all'));
        onClose(false);
    }

    const closeModal = () => onClose(false);

    return (
        <Dialog open={open} onClose={closeModal}>
            <DialogContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    Download query
                </Typography>
                <CancelOutlined sx={{ cursor: 'pointer' }} onClick={closeModal}/>
            </DialogContent>

            <DialogContent id="warning-list-wrapper" sx={{ paddingY: 0 }}>
                {warnings.map(warning => {
                    if (!warning.condition) return null
                    return (
                        <Alert key={warning.title} severity="warning" sx={{ padding: 2, marginTop: 3 }}>
                            <Stack gap={1}>
                                <Typography variant="body2"
                                    sx={{ fontWeight: 'bold' }}>
                                    {warning.title}
                                </Typography>
                                <Typography variant="body2">
                                    {warning.text}
                                </Typography>
                            </Stack>
                        </Alert>)
                })}
            </DialogContent>

            <DialogContent>
                {withShadowData ? <Box>
                    <FormControlLabel
                        control={
                            <Checkbox
                                sx={{
                                    color: colours.orange.orange,
                                    "&.Mui-checked": {
                                        color: colours.orange.orange,
                                    },
                                }}
                                checked={shadowMatrixIncluded}
                                onChange={() => setShadowMatrixIncluded(!shadowMatrixIncluded)}
                            />
                        }
                        label={
                            <Typography sx={{ fontSize: "12px", fontWeight: 600 }}>
                                    Include reason for missing data (in separate CSV)
                            </Typography>
                        }
                    />
                </Box> :
                    <Typography sx={{ fontWeight: 600, marginBottom: 2 }}>Download ZIP file with results from
                        the selected parameters.</Typography>}
                <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
                    <Button variant="contained" onClick={closeModal}>Edit Query</Button>
                    <Button variant="primary" onClick={runQuery}>Run Query</Button>
                </Box>
            </DialogContent>
        </Dialog>
    )
}

export default RunQueryModal;